import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBuilding, FaStar, FaTools, FaUsers, FaCheckCircle, FaMapMarkerAlt, FaShieldAlt, FaPhone, FaHeadset, FaChevronLeft, FaChevronRight, FaHome, FaBriefcase, FaIndustry } from 'react-icons/fa';
import './body.css';
import { Link } from "react-router-dom";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const statItemVariants = {
  hidden: { opacity: 0, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "backOut"
    }
  }
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0, 0, 63, 0.2)",
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  tap: {
    scale: 0.98
  }
};

// Aluminium Products Data
const productsData = [
  {
    title: "Aluminium Windows",
    img: "/window3.JPG",
    desc: "Energy-efficient aluminium window systems with thermal breaks, double glazing options, and various powder-coated finishes. Perfect for both residential and commercial applications with superior durability and weather resistance."
  },
  {
    title: "Aluminium Partitions",
    img: "/partition1.jpg",
    desc: "Modern office partitions and space dividers made from high-quality aluminium frames with glass or composite panel options. Create flexible workspaces with sound insulation and sleek contemporary designs."
  },
  {
    title: "Curtain Walls",
    img: "/curtainwall.webp",
    desc: "Architectural curtain wall systems providing stunning glass facades for commercial buildings. Our systems offer excellent thermal performance, weather sealing, and structural integrity while maximizing natural light."
  },
  {
    title: "Polycarbonate Sheet Roofs & Canopies Ceilings",
    img: "/roof2.JPG",
    desc: "Durable polycarbonate roofing solutions and canopy ceilings that provide UV protection, weather resistance, and excellent light diffusion. Ideal for carports, walkways, patios, and commercial outdoor spaces."
  },
  {
    title: "Aluminium Pantry Cupboards",
    img: "/pantry cupboard.jpg",
    desc: "Modern kitchen and pantry cupboard systems made from premium aluminium with powder-coated finishes. Features include soft-close mechanisms, adjustable shelves, and moisture-resistant construction for longevity."
  },
  {
    title: "Glass Partition, Glass Doors & Windows",
    img: "/partition3.JPG",
    desc: "Elegant glass partition systems, frameless glass doors, and large-format glass windows. Create transparent, light-filled spaces with tempered safety glass, various transparency options, and sleek hardware."
  }
];

// Testimonials data
const testimonials = [
  {
    id: 1,
    name: "Mr. Perera",
    rating: 5,
    review: "Excellent work on our office building! The aluminium fabrication was top-quality and completed on schedule. Highly recommended!",
    // date: "1 month ago",
    project: "Commercial Office Complex"
  },
  {
    id: 2,
    name: "Sunrise Developers",
    rating: 5,
    review: "We've used The INNOVATIVE PRE-FAB TECHNOLOGY for multiple projects. Their aluminium solutions always exceed expectations in quality and design.",
    // date: "2 months ago",
    project: "Residential Apartments"
  },
  {
    id: 3,
    name: "Mrs. Silva",
    rating: 4,
    review: "Beautiful aluminium windows for our house. The team was professional and installation was smooth. Minor delay but overall excellent service.",
    // date: "3 weeks ago",
    project: "Residential House"
  },
  {
    id: 4,
    name: "City Mall Management",
    rating: 5,
    review: "Best aluminium fabrication company in Sri Lanka! Their curtain wall system transformed our shopping complex. Quality is outstanding.",
    // date: "3 months ago",
    project: "Shopping Mall"
  },
  {
    id: 5,
    name: "Tech Park Ltd",
    rating: 5,
    review: "Outstanding service! The aluminium partitions and glass facades for our IT park were installed perfectly. Great attention to detail.",
    // date: "2 weeks ago",
    project: "IT Park"
  },
  {
    id: 6,
    name: "Hotel Grand Lanka",
    rating: 4,
    review: "Consistently good quality. Have used them for multiple hotel renovation projects. Professional team and durable products.",
    date: "4 months ago",
    project: "Five-Star Hotel"
  }
];

function Body () {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [direction, setDirection] = useState(0);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentTestimonial]);

  const nextTestimonial = () => {
    setDirection(1);
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setDirection(-1);
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index) => {
    setDirection(index > currentTestimonial ? 1 : -1);
    setCurrentTestimonial(index);
  };

  // Animation variants for carousel
  const carouselVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  // Function to render star ratings
  const renderStars = (rating) => {
    return (
      <div className="stars">
        {[...Array(5)].map((_, i) => (
          <FaStar 
            key={i} 
            className={i < rating ? "star filled" : "star"} 
          />
        ))}
      </div>
    );
  };



  // Features data
const features = [
  {
    icon: <FaBuilding />,
    title: "Premium Quality",
    description: "High-grade aluminium-tempered glass systems, steel frameworks,  engineered for strength, durability, and modern aesthetics"
  },
  {
    icon: <FaShieldAlt />,
    title: "Certified Materials",
    description: "ISO-certified aluminium profiles, structural steel, and safety glass with superior weather and load resistance"
  },
  {
    icon: <FaTools />,
    title: "Expert Craftsmanship",
    description: "Skilled technicians specializing in precision fabrication and installation of aluminium-glass and steel structures"
  },
  {
    icon: <FaHeadset />,
    title: "Project Support",
    description: "Complete project management from concept design to installation and after-sales services."
  }
];



  return (
    <div className="aluminium">
{/* SEO Meta Structure */}
<div className="seo-content" style={{display: 'none'}}>
  <h1>The INNOVATIVE PRE-FAB TECHNOLOGY – Aluminium, Steel & Glass Fabrication in Sri Lanka</h1>
  
  <h2>Premium Pre-Fabricated Building Solutions in Sri Lanka</h2>
  
  <p>
    We specialize in innovative pre-fab technology, delivering high-quality aluminium, steel, and glass fabrication
    for houses, offices, and commercial buildings across Sri Lanka. Transform your space with modern, durable,
    and energy-efficient construction solutions.
  </p>
  
  <ul>
    <li>Aluminium windows and doors Sri Lanka</li>
    <li>Structural steel fabrication</li>
    <li>Glass partitions and facades</li>
    <li>Pre-fabricated buildings Sri Lanka</li>
    <li>Curtain wall systems</li>
    <li>Commercial aluminium & steel works</li>
    <li>Tempered and laminated safety glass</li>
    <li>Architectural aluminium systems</li>
    <li>Modern facade solutions</li>
  </ul>
</div>


      {/* Hero Banner Section */}
      <section className="hero-banner">
        <div className="container">
          <div className="banner-content">
            <motion.div 
              className="banner-text"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
            <motion.h1 variants={itemVariants}>
              PREMIUM <br />PRE-FABRICATION SOLUTIONS <br />IN SRI LANKA
            </motion.h1>

            <motion.p variants={itemVariants}>
              Enhance your building’s aesthetic with <b>INNOVATIVE PRE-FAB TECHNOLOGY</b>. 
              We deliver high-quality aluminium-glass and steel  fabrication solutions for 
              houses, offices, and commercial spaces across Sri Lanka.
            </motion.p>

    
            </motion.div>
            <motion.div 
              className="banner-image"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img src="/bodyimage1.png" alt="aluminium fabrication Colombo" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="who-we-are">
        <div className="container">
          <div className="section-content">
            <motion.div 
              className="text-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={containerVariants}
            >
              <motion.h2 variants={itemVariants}>
  About The INNOVATIVE PRE-FAB TECHNOLOGY
</motion.h2>

<motion.p variants={itemVariants}>
  The INNOVATIVE PRE-FAB TECHNOLOGY is a well-renowned name in Sri Lanka for 
  aluminium-glass, and steel fabrication solutions. We enhance the appearance 
  and structural performance of houses, office buildings, commercial complexes 
  with our modern pre-fabricated systems and Green Houses.
</motion.p>

<motion.p variants={itemVariants}>
  Our journey is built on a foundation of excellence, innovation, and customer 
  satisfaction. We continuously evolve to meet the changing demands of the 
  construction industry while maintaining the highest standards of quality, 
  safety, and craftsmanship in every project we deliver.
</motion.p>


              
              <motion.div 
                className="stats-grid"
                variants={containerVariants}
              >
                <motion.div className="stat-card" variants={statItemVariants}>
                  <h2 className="stat-number-aluminium">1000+</h2>
                  <p className="stat-label-aluminium">Projects Completed</p>
                </motion.div>

                <motion.div className="stat-card" variants={statItemVariants}>
                  <h2 className="stat-number-aluminium">30+</h2>
                  <p className="stat-label-aluminium">Years Experience</p>
                </motion.div>

                {/* <motion.div className="stat-card" variants={statItemVariants}>
                  <h2 className="stat-number-aluminium">50+</h2>
                  <p className="stat-label-aluminium">Expert Technicians</p>
                </motion.div> */}

                <motion.div className="stat-card" variants={statItemVariants}>
                  <h2 className="stat-number-aluminium">100%</h2>
                  <p className="stat-label-aluminium">Client Satisfaction</p>
                </motion.div>
              </motion.div>
            </motion.div>

            <motion.div 
              className="image-content"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src="/bodyfirstimg.png" alt="Innovative Pre-Fab Technology Factory" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us">
        <div className="container">
          <div className="section-content">
            <motion.div 
              className="image-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src="/bodyimg2.png" alt="Why Choose Our Aluminium Solutions" />
            </motion.div>
            <motion.div 
              className="text-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={containerVariants}
            >
 <motion.h2 variants={itemVariants}>
  Why Choose Our Pre-Fab Solutions?
</motion.h2>

<motion.p variants={itemVariants}>
  At The INNOVATIVE PRE-FAB TECHNOLOGY, we deliver complete pre-fabricated 
  aluminium-glass and steel solutions that combine aesthetics, functionality, 
  and long-lasting durability for modern residential and commercial buildings.
</motion.p>

              
              <motion.div className="features-aluminium-list" variants={containerVariants}>
                {features.map((feature, index) => (
                  <motion.div 
                    key={index} 
                    className="feature-aluminium-item"
                    variants={itemVariants}
                  >
                    <div className="feature-aluminium-icon">{feature.icon}</div>
                    <div className="feature-aluminium-text">
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="aluminium-services">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>COMPREHENSIVE ALUMINIUM SOLUTIONS</h2>
            <p>From residential to commercial projects, we provide end-to-end aluminium fabrication services</p>
          </motion.div>
          
          <div className="services-grid-wrapper">
            <motion.div 
              className="services-grid"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={containerVariants}
            >
              <motion.div className="service-card" variants={cardVariants}>
                <FaHome className="service-icon" />
                <h3>Residential Projects</h3>
                <p>Custom aluminium windows, doors, and partitions for houses, apartments, and villas</p>
                {/* <motion.button className="btn-card" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/residential">View Projects</Link>
                </motion.button> */}
              </motion.div>

              <motion.div className="service-card" variants={cardVariants}>
                <FaBriefcase className="service-icon" />
                <h3>Commercial Buildings</h3>
                <p>Office partitions, curtain walls, and façade systems for corporate buildings</p>
                {/* <motion.button className="btn-card" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/commercial">View Projects</Link>
                </motion.button> */}
              </motion.div>

              <motion.div className="service-card" variants={cardVariants}>
                <FaIndustry className="service-icon" />
                <h3>Industrial Solutions</h3>
                <p>Factory partitions, warehouse doors, and industrial aluminium structures</p>
                {/* <motion.button className="btn-card" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/industrial">View Projects</Link>
                </motion.button> */}
              </motion.div>

              <motion.div className="service-card" variants={cardVariants}>
                <FaBuilding className="service-icon" />
                <h3>Custom Fabrication</h3>
                <p>Bespoke aluminium solutions tailored to your specific architectural requirements</p>
                {/* <motion.button className="btn-card" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/custom">Get Quote</Link>
                </motion.button> */}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
{/* Our Products Section */}
<section className="products-section">
  <div className="container">
    <h2 className="products-title">Our Pre-Fab Fabrication Products</h2>
    <p className="products-subtitle">
      Premium aluminium, steel & glass solutions for modern architecture
    </p>

    <div className="products-grid">
      {productsData.map((product, index) => (
        <motion.div 
          className="product-card" 
          key={index}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          viewport={{ once: true }}
        >
          <h4>{product.title}</h4>
          <img 
            src={product.img} 
            alt={product.title} 
            className="product-img" 
          />
          <p>{product.desc}</p>

          <div className="product-buttons">
            <Link to="/contactus">
              <button className="product-btn-inquire">
                Inquire Now
              </button>
            </Link>
            <span>or</span>
            <a 
              className="product-call-link" 
              href="tel:+94777324632"
            >
              Call for Details
            </a>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
</section>


      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What Our Clients Say</h2>
            <p>Trusted by leading builders and architects across Sri Lanka</p>
          </motion.div>
          
          <div className="testimonials-carousel">
            <div className="carousel-container">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentTestimonial}
                  custom={direction}
                  variants={carouselVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                  className="testimonial-card active"
                >
                  <div className="testimonial-header">
                    {renderStars(testimonials[currentTestimonial].rating)}
                    <div className="project-badge">
                      {testimonials[currentTestimonial].project}
                    </div>
                  </div>
                  
                  <p className="testimonial-text">"{testimonials[currentTestimonial].review}"</p>
                  
                  <div className="testimonial-footer">
                    <span className="customer-name">{testimonials[currentTestimonial].name}</span>
                    <span className="review-date">{testimonials[currentTestimonial].date}</span>
                  </div>
                </motion.div>
              </AnimatePresence>

              <button className="carousel-nav carousel-prev" onClick={prevTestimonial}>
                <FaChevronLeft />
              </button>
              <button className="carousel-nav carousel-next" onClick={nextTestimonial}>
                <FaChevronRight />
              </button>
            </div>

            <div className="carousel-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                  onClick={() => goToTestimonial(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>  

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
<h2>Transform Your Building with Premium Pre-Fab Solutions</h2>
<p>
  Contact us for a free consultation and quotation for your aluminium-glass and steel 
  fabrication project
</p>

            <div className="cta-buttons">
              <Link to="/contact">

              </Link>
              <a href="tel:+94777324632">
                <motion.button 
                  className="btn btn-secondary-aluminium-cta"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <FaPhone /> Call Now
                </motion.button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Body;

